/**
 * The email a signed-in buyer never gave us, asked for where it is needed.
 *
 * A phone-only signup carries `email = null` and was never asked again: the
 * login modal's more-info step does ask, but it is dismissible and production
 * records 362 arrivals against 3 submissions. So the ask moved to the point the
 * address is actually required — checkout — where the buyer is already
 * motivated and a receipt is obviously worth an address.
 *
 * Two stages, because the address is verified inline: `email` collects it and
 * mails a code, `code` checks that code and writes it to the account. Deliberately
 * at the top of step 1, before card details: an OTP interruption after someone
 * has typed a card number is a far worse place to put it.
 */
import { useCallback, useState } from 'react';
import { useSession } from 'next-auth/react';
import { sendEmailCode, confirmEmailCode } from '../../utils/email-verification';
import { getSessionId } from '../../utils/tracking-context';
import { isValidEmail } from '../../utils/email-verification';
import { trackFunnelEvent } from '../../utils/track-funnel';

export type EmailCaptureStage = 'email' | 'code' | 'verified';

export interface UseEmailCaptureOptions {
  /** Called once the address is on the account, so checkout can refresh the profile. */
  onVerified?: (email: string) => void | Promise<void>;
  /** Advisory recaptcha token, when the caller has one. */
  getRecaptchaToken?: () => Promise<string | undefined>;
  /**
   * The login funnel session to file the send under.
   *
   * Optional, and it DEFAULTS to the shared browser session rather than to
   * nothing. Both mount sites -- `pages/sell.tsx` and `pages/account/index.tsx`
   * -- passed only `onVerified`, so their sends reached the server with no
   * session and `logFunnelEvent` minted a synthetic `server-` one for each:
   * 28% of production `otp_sent` rows, each a one-event phantom "session" in
   * the login funnel. A default is the right shape here because there is always
   * a correct answer available on the client.
   */
  funnelSessionId?: string;
  /**
   * Which surface is asking — `sell`, `account`, `checkout`. Every event carries
   * it, because "does this work" has a different answer per surface and one
   * pooled number would hide that.
   */
  surface?: string;
}

export function useEmailCapture(options: UseEmailCaptureOptions = {}) {
  const { onVerified, getRecaptchaToken, surface = 'unknown' } = options;
  // Defaults to the shared browser session rather than to nothing: both mount
  // sites passed only `onVerified`, so their sends reached the server with no
  // session and `logFunnelEvent` minted a synthetic `server-` one for each.
  const funnelSessionId = options.funnelSessionId ?? getSessionId();
  // `UserProvider` seeds `profile` from `nextAuthSession.profile`, so writing the
  // address to the USERS row is not enough: until the session is refreshed, the
  // next page load reads the old JWT and asks again. Staging did exactly that —
  // the address was on the account and all three surfaces still prompted.
  const { update: updateSession } = useSession();

  const [stage, setStage] = useState<EmailCaptureStage>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Report that the ask was put in front of someone.
   *
   * Called by the component on mount rather than from here, because the hook
   * runs on every render of a page that may never show the panel. Without a
   * shown event there is no denominator, and an email-capture with no
   * denominator is exactly the hole the login modal's more-info step sat in for
   * nine months: 378 arrivals, 7 submissions, and nobody could see it.
   */
  const trackShown = useCallback(() => {
    trackFunnelEvent('email_capture', 'shown', { surface }, { component: 'email-capture', dedupKey: surface });
  }, [surface]);

  /** Collect the address and mail a code to it. */
  const requestCode = useCallback(async (): Promise<boolean> => {
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setError('invalid_email');
      trackFunnelEvent('email_capture', 'rejected', { surface, reason: 'invalid_email' }, { component: 'email-capture' });
      return false;
    }

    trackFunnelEvent('email_capture', 'code_requested', { surface }, { component: 'email-capture' });

    setBusy(true);
    setError(null);
    // Advisory only: the server scores the token and never blocks on it, so a
    // recaptcha that fails to load must not strand the buyer.
    const recaptchaToken = getRecaptchaToken ? await getRecaptchaToken() : undefined;
    const result = await sendEmailCode({ email: trimmed, recaptchaToken, funnelSessionId });
    setBusy(false);

    if (!result.success) {
      setError(result.error ?? 'send_failed');
      trackFunnelEvent('email_capture', 'send_failed', { surface, reason: result.error ?? 'send_failed' }, { component: 'email-capture', immediate: true });
      return false;
    }

    setEmail(trimmed);
    setStage('code');
    trackFunnelEvent('email_capture', 'code_sent', { surface }, { component: 'email-capture' });
    return true;
  }, [email, getRecaptchaToken, funnelSessionId, surface]);

  /** Check the code and write the address to the account. */
  const submitCode = useCallback(async (): Promise<boolean> => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('invalid_code');
      return false;
    }

    setBusy(true);
    setError(null);
    const result = await confirmEmailCode({ email, code: trimmed });
    setBusy(false);

    if (!result.success) {
      setError(result.error ?? 'invalid_code');
      trackFunnelEvent('email_capture', 'code_failed', { surface, reason: result.error ?? 'invalid_code' }, { component: 'email-capture', immediate: true });
      return false;
    }

    setStage('verified');
    // `complete`, not a name of its own: this is the bottom of the funnel and
    // the shared dashboards key the last node on that literal.
    trackFunnelEvent('email_capture', 'complete', { surface }, { component: 'email-capture', immediate: true });
    await updateSession();
    await onVerified?.(email);
    return true;
  }, [code, email, onVerified, updateSession, surface]);

  /** Back to the address field — a mistyped address cannot be corrected otherwise. */
  const editEmail = useCallback(() => {
    setStage('email');
    setCode('');
    setError(null);
    trackFunnelEvent('email_capture', 'edit_email', { surface }, { component: 'email-capture' });
  }, [surface]);

  return {
    stage,
    email,
    setEmail,
    code,
    setCode,
    error,
    busy,
    requestCode,
    submitCode,
    editEmail,
    trackShown,
    /** Checkout may not proceed until this is true. */
    isVerified: stage === 'verified',
  };
}
